import { Suspense, useCallback, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { Spinner } from '@/components/ui';
// Submodule paths, not the `@/features/inactivity` barrel's eager re-exports of everything: the
// shell needs the bell, the gate and nothing else of the feature.
import { InactivityLockedScreen } from '@/features/inactivity/InactivityLockedScreen';
import { useLockedStatus } from '@/features/inactivity/queries';
import { useEdgeColor } from '@/lib/hooks';
import { useThemePreference } from '@/lib/theme';

import { useAuth } from '@/app/providers/authContext';

import { CommandPalette } from './CommandPalette';
import { profilesForNavigationItem } from './navigation';
import { InternalFooter } from './internal/InternalFooter';
import { InternalHeader } from './internal/InternalHeader';
import { StoreFooter } from './store/StoreFooter';
import { StoreHeader } from './store/StoreHeader';
import { useStoreSummary } from './store/useStoreSummary';
import { Topbar } from './Topbar';
import { useCommandPaletteShortcut } from './useCommandPaletteShortcut';
import { WelcomeBanner } from './WelcomeBanner';
import styles from './AppShell.module.css';

const MAIN_CONTENT_ID = 'conteudo-principal';

/**
 * Authenticated layout. One top bar for every profile, with the page switcher at its centre: a
 * customer's two destinations (Comprar, Pedidos) and an internal profile's modules alike. The
 * backoffice's own sections are not the app's navigation, so they live inside the backoffice
 * (`BackofficeRoute`), as a sidebar or, for a profile with only a few, a row of tabs.
 *
 * ⌘K ("Ir para") lives here, with a search button in the bar, so there is one shortcut and one
 * palette.
 */
export function AppShell() {
  const { profile } = useAuth();
  // The profiles that buy get the storefront chrome of the redesign (Figma "Esboço · nova home do
  // cliente"); the internal profiles keep the top bar until their own redesign lands.
  const buys = profile !== null && profilesForNavigationItem('cart').includes(profile);
  if (buys) return <StoreShell />;
  // A profile with no claim keeps the plain top bar: it reaches nothing but "Minha organização".
  return profile === null ? <TopbarShell /> : <InternalShell />;
}

function TopbarShell() {
  useEdgeColor();
  const [, setTheme] = useThemePreference();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const openPalette = useCallback(() => {
    setPaletteOpen(true);
  }, []);
  useCommandPaletteShortcut(openPalette);
  const locked = useLockedStatus();

  // Every unit blocked for inactivity and the policy locking the login with them: the person is
  // signed in, but every tenant route refuses, so the shell gives way to the one screen that can
  // still do something — ask for the units back (Figma I7).
  if (locked !== undefined) {
    return <InactivityLockedScreen status={locked} />;
  }

  return (
    <div className={styles.shell}>
      <a className={styles.skipLink} href={`#${MAIN_CONTENT_ID}`}>
        Pular para o conteúdo principal
      </a>

      <div className={styles.topbarArea}>
        <Topbar onSearch={openPalette} />
      </div>

      <main className={styles.main} id={MAIN_CONTENT_ID} tabIndex={-1}>
        <div className={styles.content}>
          <WelcomeBanner onSearch={openPalette} />
          <Suspense fallback={<Spinner size="lg" label="Carregando a tela…" />}>
            <Outlet />
          </Suspense>
        </div>
      </main>

      {/* The signed-in shell shows the build too, not only the login screen. Someone
          reviewing a frozen release browses the catalogue rather than the login page, and
          "which version am I looking at?" has to be answerable from where they are. */}
      <footer className={styles.footer}>
        Instoque · versão <code>{import.meta.env.VITE_APP_VERSION}</code>
      </footer>

      <CommandPalette
        open={paletteOpen}
        onClose={() => {
          setPaletteOpen(false);
        }}
        onTheme={setTheme}
      />
    </div>
  );
}

/**
 * The storefront shell (Figma D1, M1): the red header with the search, the page on the warm
 * "página" ground, the full footer. ⌘K focuses the search here (`StoreHeader`); "Ajuda" opens the
 * palette, which still lists every page and the theme. The home's red band greets the buyer, so
 * the `WelcomeBanner` of the internal shell is not drawn.
 */
function StoreShell() {
  useEdgeColor();
  const [, setTheme] = useThemePreference();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const openPalette = useCallback(() => {
    setPaletteOpen(true);
  }, []);
  const locked = useLockedStatus();
  const summary = useStoreSummary();

  if (locked !== undefined) {
    return <InactivityLockedScreen status={locked} />;
  }

  return (
    <div className={[styles.shell, styles.store].join(' ')}>
      <a className={styles.skipLink} href={`#${MAIN_CONTENT_ID}`}>
        Pular para o conteúdo principal
      </a>

      <div className={styles.topbarArea}>
        <StoreHeader summary={summary} onHelp={openPalette} />
      </div>

      <main className={styles.main} id={MAIN_CONTENT_ID} tabIndex={-1}>
        <div className={styles.content}>
          <Suspense fallback={<Spinner size="lg" label="Carregando a tela…" />}>
            <Outlet />
          </Suspense>
        </div>
      </main>

      <StoreFooter summary={summary} onHelp={openPalette} />

      <CommandPalette
        open={paletteOpen}
        onClose={() => {
          setPaletteOpen(false);
        }}
        onTheme={setTheme}
      />
    </div>
  );
}

/**
 * The internal profiles' shell (Figma "Esboço · perfis internos"): the red header with "Todas as
 * áreas", the page on the "página" ground and the internal footer. The backoffice's own sidebar
 * and tabs are gone — "Todas as áreas" and the shortcuts are the navigation now. The welcome
 * banner stays: it is how an internal profile meets ⌘K on its first sign-in.
 */
function InternalShell() {
  useEdgeColor();
  const [, setTheme] = useThemePreference();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const openPalette = useCallback(() => {
    setPaletteOpen(true);
  }, []);

  return (
    <div className={[styles.shell, styles.store].join(' ')}>
      <a className={styles.skipLink} href={`#${MAIN_CONTENT_ID}`}>
        Pular para o conteúdo principal
      </a>
      <div className={styles.topbarArea}>
        <InternalHeader onHelp={openPalette} />
      </div>
      <main className={styles.main} id={MAIN_CONTENT_ID} tabIndex={-1}>
        <div className={styles.content}>
          <WelcomeBanner onSearch={openPalette} />
          <Suspense fallback={<Spinner size="lg" label="Carregando a tela…" />}>
            <Outlet />
          </Suspense>
        </div>
      </main>
      <InternalFooter />
      <CommandPalette
        open={paletteOpen}
        onClose={() => {
          setPaletteOpen(false);
        }}
        onTheme={setTheme}
      />
    </div>
  );
}
